import type { ZodType } from "zod";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiSchemaError extends Error {
  constructor(
    message: string,
    public readonly cause: unknown,
  ) {
    super(message);
    this.name = "ApiSchemaError";
  }
}

export type Method = "GET" | "POST" | "DELETE" | "PATCH";

export interface RequestInit {
  method?: Method;
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(fn: () => void): void {
  onUnauthorized = fn;
}

export function invokeOnUnauthorized(): void {
  if (onUnauthorized) onUnauthorized();
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
  schema?: ZodType<T>,
): Promise<T> {
  const { method = "GET", body, form, signal, query } = init;

  // Build URL with query params, dropping undefined values
  let url = path;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) {
        params.set(k, String(v));
      }
    }
    const qs = params.toString();
    if (qs) url += "?" + qs;
  }

  const headers: Record<string, string> = {};
  let fetchBody: BodyInit | null = null;

  if (form) {
    // Multipart/form-data — do NOT set Content-Type, browser sets boundary
    fetchBody = form;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    fetchBody = JSON.stringify(body);
  }

  const response = await fetch(url, {
    method,
    headers,
    body: fetchBody,
    credentials: "include",
    signal: signal ?? null,
  });

  if (!response.ok) {
    let code = `http_${response.status}`;
    let message = response.statusText || String(response.status);
    let fields: Record<string, string> | undefined;

    try {
      const err = (await response.json()) as {
        detail?: unknown;
        message?: string;
        fields?: Record<string, string>;
      };
      if (typeof err.detail === "string") {
        code = err.detail;
        message = err.detail;
      } else if (typeof err.message === "string") {
        message = err.message;
      }
      if (err.fields != null && typeof err.fields === "object") {
        fields = err.fields as Record<string, string>;
      }
    } catch {
      // Keep defaults on parse error
    }

    if (response.status === 401 && onUnauthorized) {
      onUnauthorized();
    }

    throw new ApiError(response.status, code, message, fields);
  }

  // No-content responses
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return undefined as T;
  }

  const json = (await response.json()) as T;

  // Dev-only schema validation to catch backend drift early
  if (import.meta.env.DEV && schema) {
    try {
      return schema.parse(json) as T;
    } catch (err) {
      throw new ApiSchemaError(
        `Schema validation failed for ${path}: ${String(err)}`,
        err,
      );
    }
  }

  return json;
}
